import React, { useState, useEffect } from 'react';
import { Target, Plus, Search, Filter, Grid3x3, List, X, Edit, Trash2, CheckCircle, Phone, Mail, MapPin, Calendar, Building2, User, Globe, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sektör seçenekleri
const SECTORS = [
  'Teknoloji', 'Finans', 'Sağlık', 'Eğitim', 'İnşaat', 'Üretim',
  'Perakende', 'Hizmet', 'Lojistik', 'Enerji', 'Gıda', 'Turizm', 'Diğer'
];

// Kaynak seçenekleri
const SOURCES = [
  'Website', 'Referans', 'Etkinlik / Fuar', 'LinkedIn', 'Instagram',
  'Facebook', 'Google Ads', 'Telefon Araması', 'Email Kampanyası', 'Diğer'
];

// Ülkeler
const COUNTRIES = [
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷', phone: '+90' },
  { code: 'DE', name: 'Almanya', flag: '🇩🇪', phone: '+49' },
  { code: 'US', name: 'ABD', flag: '🇺🇸', phone: '+1' },
  { code: 'GB', name: 'İngiltere', flag: '🇬🇧', phone: '+44' },
  { code: 'FR', name: 'Fransa', flag: '🇫🇷', phone: '+33' },
];

// Türkiye şehirleri
const TR_CITIES = [
  'İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana',
  'Konya', 'Gaziantep', 'Şanlıurfa', 'Kocaeli', 'Mersin', 'Diyarbakır'
];

const Leads = () => {
  // State
  const [leads, setLeads] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, this_month: 0, this_week: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  
  // Modals
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    sector: '',
    company_phone_country_code: '+90',
    company_phone: '',
    company_email: '',
    website: '',
    country: 'TR',
    city: '',
    address: '',
    postal_code: '',
    contacts: [{
      id: Date.now().toString(),
      full_name: '',
      title: '',
      phone_country_code: '+90',
      phone: '',
      email: '',
      linkedin: '',
      notes: ''
    }],
    source: '',
    first_contact_date: new Date().toISOString().split('T')[0],
    general_notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);
  
  // Convert form state
  const [convertFormData, setConvertFormData] = useState({
    company_legal_name: '',
    tax_office: '',
    tax_number: ''
  });

  // Fetch leads
  useEffect(() => {
    fetchLeads();
  }, [page, perPage, searchQuery, sectorFilter, countryFilter, cityFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (sectorFilter) params.append('sector', sectorFilter);
      if (countryFilter) params.append('country', countryFilter);
      if (cityFilter) params.append('city', cityFilter);
      
      const response = await axios.get(`${API}/crm/leads?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        setLeads(response.data.leads);
        setStatistics(response.data.statistics);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('❌ Error fetching leads:', error);
      toast.error('Müşteri adayları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle contact change
  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index][field] = value;
    setFormData(prev => ({ ...prev, contacts: newContacts }));
  };

  // Add new contact
  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, {
        id: Date.now().toString(),
        full_name: '',
        title: '',
        phone_country_code: '+90',
        phone: '',
        email: '',
        linkedin: '',
        notes: ''
      }]
    }));
  };

  // Remove contact
  const removeContact = (index) => {
    const newContacts = formData.contacts.filter((_, i) => i !== index);
    // Keep at least one empty contact slot
    if (newContacts.length === 0) {
      newContacts.push({
        id: Date.now().toString(),
        full_name: '',
        title: '',
        phone_country_code: '+90',
        phone: '',
        email: '',
        linkedin: '',
        notes: ''
      });
    }
    setFormData(prev => ({ ...prev, contacts: newContacts }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name || !formData.sector || !formData.country || !formData.city) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    
    // Filter out empty contacts before sending
    const validContacts = formData.contacts.filter(c => c.full_name || c.phone || c.email);
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/crm/leads`, {
        ...formData,
        contacts: validContacts
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        toast.success('✅ Müşteri adayı başarıyla oluşturuldu');
        setShowNewLeadModal(false);
        resetForm();
        fetchLeads();
      }
    } catch (error) {
      console.error('❌ Error creating lead:', error);
      toast.error(error.response?.data?.detail || 'Müşteri adayı oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      company_name: '',
      sector: '',
      company_phone_country_code: '+90',
      company_phone: '',
      company_email: '',
      website: '',
      country: 'TR',
      city: '',
      address: '',
      postal_code: '',
      contacts: [{
        id: Date.now().toString(),
        full_name: '',
        title: '',
        phone_country_code: '+90',
        phone: '',
        email: '',
        linkedin: '',
        notes: ''
      }],
      source: '',
      first_contact_date: new Date().toISOString().split('T')[0],
      general_notes: ''
    });
  };

  // Delete lead
  const handleDelete = async (leadId) => {
    if (!window.confirm('Bu müşteri adayını silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Müşteri adayı silindi');
      fetchLeads();
    } catch (error) {
      console.error('❌ Error deleting lead:', error);
      toast.error('Silme işlemi başarısız');
    }
  };

  // Convert to customer
  const handleConvertToCustomer = async () => {
    if (!selectedLead) return;
    
    // Validation
    if (!convertFormData.company_legal_name || !convertFormData.tax_number) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    
    try {
      setConverting(true);
      const response = await axios.post(
        `${API}/crm/leads/${selectedLead.id}/convert-to-customer`,
        convertFormData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        toast.success('🎉 Müşteri adayı başarıyla müşteriye çevrildi!');
        setShowConvertModal(false);
        setSelectedLead(null);
        setConvertFormData({ company_legal_name: '', tax_office: '', tax_number: '' });
        fetchLeads();
      }
    } catch (error) {
      console.error('❌ Error converting:', error);
      toast.error(error.response?.data?.detail || 'Dönüştürme başarısız');
    } finally {
      setConverting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setSectorFilter('');
    setCountryFilter('');
    setCityFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || sectorFilter || countryFilter || cityFilter;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          borderLeft: '4px solid #3B82F6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '12px',
              backgroundColor: '#DBEAFE', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '28px'
            }}>👥</div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3B82F6' }}>{statistics.total}</div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Toplam Aday</div>
            </div>
          </div>
        </div>

        <div style={{
          padding: '20px', backgroundColor: 'white', borderRadius: '12px',
          borderLeft: '4px solid #10B981', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '12px',
              backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '28px'
            }}>📅</div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10B981' }}>{statistics.this_month}</div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Bu Ay Eklenen</div>
            </div>
          </div>
        </div>

        <div style={{
          padding: '20px', backgroundColor: 'white', borderRadius: '12px',
          borderLeft: '4px solid #F59E0B', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '12px',
              backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '28px'
            }}>🎯</div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#F59E0B' }}>{statistics.active}</div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Aktif Takip</div>
            </div>
          </div>
        </div>

        <div style={{
          padding: '20px', backgroundColor: 'white', borderRadius: '12px',
          borderLeft: '4px solid #8B5CF6', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '12px',
              backgroundColor: '#E9D5FF', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '28px'
            }}>💼</div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8B5CF6' }}>{statistics.this_week}</div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Bu Hafta Eklenen</div>
            </div>
          </div>
        </div>
      </div>

      {/* Başlık ve Yeni Lead Butonu */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
            🎯 Müşteri Adayları
          </h1>
          <p style={{ color: '#6B7280' }}>Potansiyel müşterilerinizi yönetin</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={async () => {
              try {
                const response = await axios.post(`${API}/crm/leads/seed-test-data`, {}, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.data.success) {
                  toast.success(response.data.message);
                  fetchLeads();
                }
              } catch (error) {
                console.error('❌ Error seeding data:', error);
                toast.error('Test verileri yüklenemedi');
              }
            }}
            style={{
              padding: '12px 24px', backgroundColor: '#10B981', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
            }}
          >
            🧪 Test Verisi Yükle
          </button>
          <button
            onClick={() => setShowNewLeadModal(true)}
            style={{
              padding: '12px 24px', backgroundColor: '#3B82F6', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
            }}
          >
            <Plus size={20} />
            Yeni Aday Ekle
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          {/* Arama */}
          <div style={{ flex: '1 1 300px', position: 'relative' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} size={20} />
            <input
              type="text"
              placeholder="Aday ara..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              style={{
                width: '100%', padding: '10px 10px 10px 44px',
                border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px'
              }}
            />
          </div>

          {/* Sektör Filtresi */}
          <select
            value={sectorFilter}
            onChange={(e) => { setSectorFilter(e.target.value); setPage(1); }}
            style={{
              padding: '10px 16px', border: '1px solid #E5E7EB',
              borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
            }}
          >
            <option value="">Tüm Sektörler</option>
            {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* View Toggle */}
          <div style={{ display: 'flex', gap: '4px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '4px' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: '6px',
                backgroundColor: viewMode === 'grid' ? '#EFF6FF' : 'transparent',
                color: viewMode === 'grid' ? '#3B82F6' : '#6B7280',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <Grid3x3 size={18} /> Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: '6px',
                backgroundColor: viewMode === 'list' ? '#EFF6FF' : 'transparent',
                color: viewMode === 'list' ? '#3B82F6' : '#6B7280',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <List size={18} /> Liste
            </button>
          </div>

          {/* Per Page */}
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            style={{
              padding: '10px 16px', border: '1px solid #E5E7EB',
              borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
            }}
          >
            <option value={25}>25 satır</option>
            <option value={50}>50 satır</option>
            <option value={100}>100 satır</option>
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '10px 16px', border: '1px solid #EF4444',
                borderRadius: '8px', backgroundColor: '#FEF2F2',
                color: '#EF4444', fontSize: '14px', fontWeight: '500',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <X size={18} /> Sıfırla
            </button>
          )}
        </div>
      </div>

      {/* Leads List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p style={{ marginTop: '16px', color: '#6B7280' }}>Yükleniyor...</p>
        </div>
      ) : leads.length === 0 ? (
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '60px',
          textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <Target size={64} style={{ color: '#9CA3AF', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            Henüz müşteri adayı yok
          </h3>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>İlk müşteri adayınızı ekleyerek başlayın</p>
          <button
            onClick={() => setShowNewLeadModal(true)}
            style={{
              padding: '12px 24px', backgroundColor: '#3B82F6', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '14px',
              fontWeight: '500', cursor: 'pointer'
            }}
          >
            + Yeni Aday Ekle
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
          {leads.map(lead => (
            <div key={lead.id} style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #3B82F6', transition: 'all 0.2s', cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}>
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                  {lead.company_name}
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280' }}>{lead.sector}</p>
              </div>
              
              {lead.contacts && lead.contacts[0] && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    {lead.contacts[0].full_name}
                  </div>
                  {lead.contacts[0].title && (
                    <div style={{ fontSize: '13px', color: '#9CA3AF' }}>{lead.contacts[0].title}</div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {lead.contacts && lead.contacts[0]?.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <Phone size={16} />
                    <span>{lead.contacts[0].phone_country_code} {lead.contacts[0].phone}</span>
                  </div>
                )}
                {lead.contacts && lead.contacts[0]?.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <Mail size={16} />
                    <span>{lead.contacts[0].email}</span>
                  </div>
                )}
                {lead.city && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <MapPin size={16} />
                    <span>{lead.city}, {COUNTRIES.find(c => c.code === lead.country)?.name}</span>
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setSelectedLead(lead);
                    setShowDetailModal(true);
                  }}
                  style={{
                    flex: 1, padding: '8px', backgroundColor: '#3B82F6',
                    color: 'white', border: 'none', borderRadius: '6px',
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  <Eye size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Detay
                </button>
                <button
                  onClick={() => {
                    setSelectedLead(lead);
                    setShowConvertModal(true);
                  }}
                  style={{
                    flex: 1, padding: '8px', backgroundColor: '#10B981',
                    color: 'white', border: 'none', borderRadius: '6px',
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  <CheckCircle size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Çevir
                </button>
                <button
                  onClick={() => handleDelete(lead.id)}
                  style={{
                    padding: '8px 12px', backgroundColor: '#FEE2E2',
                    color: '#EF4444', border: 'none', borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              <tr>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>Şirket</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>Sektör</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>Yetkili</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>İletişim</th>
                <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>Konum</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id} style={{ borderBottom: '1px solid #E5E7EB' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                  <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>{lead.company_name}</td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6B7280' }}>{lead.sector}</td>
                  <td style={{ padding: '16px' }}>
                    {lead.contacts && lead.contacts[0] && (
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>{lead.contacts[0].full_name}</div>
                        {lead.contacts[0].title && <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{lead.contacts[0].title}</div>}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px' }}>
                    {lead.contacts && lead.contacts[0] && (
                      <div style={{ fontSize: '13px', color: '#6B7280' }}>
                        <div>{lead.contacts[0].phone_country_code} {lead.contacts[0].phone}</div>
                        <div>{lead.contacts[0].email}</div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6B7280' }}>{lead.city}, {COUNTRIES.find(c => c.code === lead.country)?.name}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                        style={{
                          padding: '6px 12px', backgroundColor: '#3B82F6', color: 'white',
                          border: 'none', borderRadius: '6px', fontSize: '12px',
                          fontWeight: '500', cursor: 'pointer'
                        }}
                      >
                        Detay
                      </button>
                      <button
                        onClick={() => { setSelectedLead(lead); setShowConvertModal(true); }}
                        style={{
                          padding: '6px 12px', backgroundColor: '#10B981', color: 'white',
                          border: 'none', borderRadius: '6px', fontSize: '12px',
                          fontWeight: '500', cursor: 'pointer'
                        }}
                      >
                        Çevir
                      </button>
                      <button
                        onClick={() => handleDelete(lead.id)}
                        style={{
                          padding: '6px 12px', backgroundColor: '#FEE2E2',
                          color: '#EF4444', border: 'none', borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '24px', padding: '16px', backgroundColor: 'white',
          borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280' }}>
            {((page - 1) * perPage) + 1}-{Math.min(page * perPage, total)} / {total} kayıt
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '8px 16px', border: '1px solid #E5E7EB',
                borderRadius: '6px', backgroundColor: 'white',
                color: page === 1 ? '#9CA3AF' : '#374151',
                cursor: page === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ‹ Önceki
            </button>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    style={{
                      padding: '8px 12px', border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: page === pageNum ? '#3B82F6' : 'white',
                      color: page === pageNum ? 'white' : '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '8px 16px', border: '1px solid #E5E7EB',
                borderRadius: '6px', backgroundColor: 'white',
                color: page === totalPages ? '#9CA3AF' : '#374151',
                cursor: page === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Sonraki ›
            </button>
          </div>
        </div>
      )}

      {/* Yeni Lead Modal - Devam edecek... */}
      {showNewLeadModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px',
            maxWidth: '800px', width: '100%', maxHeight: '90vh',
            overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Form içeriği bir sonraki mesajda devam edecek */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>Yeni Müşteri Adayı</h2>
                <button onClick={() => { setShowNewLeadModal(false); resetForm(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                  <X size={24} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                {/* ŞİRKET BİLGİLERİ */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '2px solid #3B82F6', paddingBottom: '8px' }}>
                    🏢 Şirket Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Şirket Adı <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange}
                        placeholder="Örn: ABC Teknoloji A.Ş."
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} required />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Sektör <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <select name="sector" value={formData.sector} onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} required>
                        <option value="">Sektör seçin</option>
                        {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Şirket Telefonu
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <select name="company_phone_country_code" value={formData.company_phone_country_code} onChange={handleInputChange}
                          style={{ width: '90px', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }}>
                          {COUNTRIES.map(c => <option key={c.code} value={c.phone}>{c.flag} {c.phone}</option>)}
                        </select>
                        <input type="tel" name="company_phone" value={formData.company_phone} onChange={handleInputChange}
                          placeholder="5xx xxx xx xx"
                          style={{ flex: 1, padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Şirket E-posta
                      </label>
                      <input type="email" name="company_email" value={formData.company_email} onChange={handleInputChange}
                        placeholder="info@sirket.com"
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Website
                      </label>
                      <input type="url" name="website" value={formData.website} onChange={handleInputChange}
                        placeholder="https://www.sirket.com"
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} />
                    </div>
                  </div>
                </div>

                {/* ADRES BİLGİLERİ */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '2px solid #10B981', paddingBottom: '8px' }}>
                    📍 Adres Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Ülke <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <select name="country" value={formData.country} onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} required>
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Şehir <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      {formData.country === 'TR' ? (
                        <select name="city" value={formData.city} onChange={handleInputChange}
                          style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} required>
                          <option value="">Şehir seçin</option>
                          {TR_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
                        </select>
                      ) : (
                        <input type="text" name="city" value={formData.city} onChange={handleInputChange}
                          placeholder="Şehir adı"
                          style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} required />
                      )}
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Adres
                      </label>
                      <textarea name="address" value={formData.address} onChange={handleInputChange}
                        placeholder="Sokak, Cadde, Mahalle, Bina No vb."
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', minHeight: '80px', resize: 'vertical' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Posta Kodu
                      </label>
                      <input type="text" name="postal_code" value={formData.postal_code} onChange={handleInputChange}
                        placeholder="34000"
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} />
                    </div>
                  </div>
                </div>

                {/* YETKİLİ KİŞİLER */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', borderBottom: '2px solid #F59E0B', paddingBottom: '8px' }}>
                      👤 Yetkili Kişiler <span style={{ fontSize: '13px', fontWeight: '400', color: '#6B7280' }}>(İsteğe bağlı)</span>
                    </h3>
                    <button type="button" onClick={addContact}
                      style={{ padding: '6px 12px', backgroundColor: '#F59E0B', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Plus size={16} /> Kişi Ekle
                    </button>
                  </div>
                  
                  {formData.contacts.map((contact, index) => (
                    <div key={contact.id} style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Kişi {index + 1}</h4>
                        {formData.contacts.length > 1 && (
                          <button type="button" onClick={() => removeContact(index)}
                            style={{ padding: '4px 8px', backgroundColor: '#FEE2E2', color: '#EF4444', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                            <X size={14} style={{ display: 'inline' }} /> Kaldır
                          </button>
                        )}
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Ad Soyad
                          </label>
                          <input type="text" value={contact.full_name} onChange={(e) => handleContactChange(index, 'full_name', e.target.value)}
                            placeholder="Örn: Ahmet Yılmaz"
                            style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Unvan
                          </label>
                          <input type="text" value={contact.title} onChange={(e) => handleContactChange(index, 'title', e.target.value)}
                            placeholder="Örn: Genel Müdür"
                            style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Telefon
                          </label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <select value={contact.phone_country_code} onChange={(e) => handleContactChange(index, 'phone_country_code', e.target.value)}
                              style={{ width: '80px', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}>
                              {COUNTRIES.map(c => <option key={c.code} value={c.phone}>{c.flag} {c.phone}</option>)}
                            </select>
                            <input type="tel" value={contact.phone} onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                              placeholder="5xx xxx xx xx"
                              style={{ flex: 1, padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }} />
                          </div>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            E-posta
                          </label>
                          <input type="email" value={contact.email} onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                            placeholder="ahmet@sirket.com"
                            style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            LinkedIn Profil
                          </label>
                          <input type="url" value={contact.linkedin} onChange={(e) => handleContactChange(index, 'linkedin', e.target.value)}
                            placeholder="https://linkedin.com/in/..."
                            style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Notlar
                          </label>
                          <textarea value={contact.notes} onChange={(e) => handleContactChange(index, 'notes', e.target.value)}
                            placeholder="Bu kişi hakkında notlar..."
                            style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px', minHeight: '60px', resize: 'vertical' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* EK BİLGİLER */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '2px solid #8B5CF6', paddingBottom: '8px' }}>
                    📝 Ek Bilgiler
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Kaynak
                      </label>
                      <select name="source" value={formData.source} onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }}>
                        <option value="">Kaynak seçin</option>
                        {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        İlk Temas Tarihi
                      </label>
                      <input type="date" name="first_contact_date" value={formData.first_contact_date} onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px' }} />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Genel Notlar
                      </label>
                      <textarea name="general_notes" value={formData.general_notes} onChange={handleInputChange}
                        placeholder="Bu müşteri adayı hakkında genel notlar..."
                        style={{ width: '100%', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', minHeight: '100px', resize: 'vertical' }} />
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                  <button type="button" onClick={() => { setShowNewLeadModal(false); resetForm(); }}
                    style={{ flex: 1, padding: '12px', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    İptal
                  </button>
                  <button type="submit" disabled={submitting}
                    style={{ flex: 1, padding: '12px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', opacity: submitting ? 0.5 : 1 }}>
                    {submitting ? 'Kaydediliyor...' : '✅ Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '20px', overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px',
            padding: '32px', maxWidth: '900px', width: '100%',
            maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
                  🏢 {selectedLead.company_name}
                </h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '4px 12px', backgroundColor: '#DBEAFE', color: '#1E40AF', borderRadius: '12px', fontSize: '13px', fontWeight: '500' }}>
                    {selectedLead.sector}
                  </span>
                  <span style={{ padding: '4px 12px', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '12px', fontSize: '13px', fontWeight: '500' }}>
                    {selectedLead.status === 'new' ? '🆕 Yeni' : selectedLead.status === 'contacted' ? '📞 Görüşüldü' : '✅ Aktif'}
                  </span>
                </div>
              </div>
              <button onClick={() => { setShowDetailModal(false); setSelectedLead(null); }}
                style={{ padding: '8px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Company Info */}
            <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                📋 Şirket Bilgileri
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {selectedLead.company_phone && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Telefon</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {selectedLead.company_phone_country_code} {selectedLead.company_phone}
                    </div>
                  </div>
                )}
                {selectedLead.company_email && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>E-posta</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedLead.company_email}</div>
                  </div>
                )}
                {selectedLead.website && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Website</div>
                    <a href={selectedLead.website} target="_blank" rel="noopener noreferrer" 
                       style={{ fontSize: '14px', color: '#3B82F6', fontWeight: '500' }}>
                      {selectedLead.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Address */}
            {(selectedLead.address || selectedLead.city || selectedLead.country) && (
              <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                  📍 Adres Bilgileri
                </h3>
                <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  {selectedLead.address && <div>{selectedLead.address}</div>}
                  <div>{selectedLead.city}, {selectedLead.country}</div>
                  {selectedLead.postal_code && <div>Posta Kodu: {selectedLead.postal_code}</div>}
                </div>
              </div>
            )}

            {/* Contacts */}
            {selectedLead.contacts && selectedLead.contacts.length > 0 && (
              <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                  👥 Yetkili Kişiler ({selectedLead.contacts.length})
                </h3>
                {selectedLead.contacts.map((contact, idx) => (
                  <div key={idx} style={{ 
                    padding: '16px', backgroundColor: 'white', borderRadius: '8px', 
                    marginBottom: idx < selectedLead.contacts.length - 1 ? '12px' : '0',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Ad Soyad</div>
                        <div style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{contact.full_name || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Unvan</div>
                        <div style={{ fontSize: '14px', color: '#111827' }}>{contact.title || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Telefon</div>
                        <div style={{ fontSize: '14px', color: '#111827' }}>
                          {contact.phone ? `${contact.phone_country_code} ${contact.phone}` : '-'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>E-posta</div>
                        <div style={{ fontSize: '14px', color: '#111827' }}>{contact.email || '-'}</div>
                      </div>
                      {contact.linkedin && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>LinkedIn</div>
                          <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" 
                             style={{ fontSize: '14px', color: '#3B82F6' }}>
                            {contact.linkedin}
                          </a>
                        </div>
                      )}
                      {contact.notes && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Notlar</div>
                          <div style={{ fontSize: '14px', color: '#374151' }}>{contact.notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Additional Info */}
            <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                📝 Ek Bilgiler
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {selectedLead.source && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Kaynak</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedLead.source}</div>
                  </div>
                )}
                {selectedLead.first_contact_date && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>İlk Temas</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {new Date(selectedLead.first_contact_date).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Oluşturulma</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {new Date(selectedLead.created_at).toLocaleDateString('tr-TR')}
                  </div>
                </div>
                {selectedLead.created_by_name && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Oluşturan</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedLead.created_by_name}</div>
                  </div>
                )}
                {selectedLead.general_notes && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Genel Notlar</div>
                    <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{selectedLead.general_notes}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowDetailModal(false); setShowConvertModal(true); }}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#10B981',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                <CheckCircle size={18} style={{ display: 'inline', marginRight: '8px' }} />
                Müşteriye Çevir
              </button>
              <button
                onClick={() => { setShowDetailModal(false); setSelectedLead(null); }}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#F3F4F6',
                  color: '#374151', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvertModal && selectedLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px',
            padding: '32px', maxWidth: '600px', width: '100%',
            maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>
              🎉 Müşteriye Çevir
            </h2>
            <p style={{ color: '#6B7280', marginBottom: '24px' }}>
              Müşteriye çevirmek için ek bilgileri doldurun
            </p>
            
            {/* Mevcut Lead Bilgileri */}
            <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px' }}><strong>📋 Aday:</strong> {selectedLead.company_name}</div>
              {selectedLead.contacts && selectedLead.contacts[0] && (
                <div style={{ marginBottom: '8px' }}><strong>👤 Yetkili:</strong> {selectedLead.contacts[0].full_name}</div>
              )}
              <div><strong>📅 Eklenme:</strong> {new Date(selectedLead.created_at).toLocaleDateString('tr-TR')}</div>
            </div>
            
            {/* Ek Bilgi Formu */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '2px solid #10B981', paddingBottom: '8px' }}>
                📄 Resmi Şirket Bilgileri
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Şirket Resmi Ünvanı <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={convertFormData.company_legal_name}
                    onChange={(e) => setConvertFormData(prev => ({ ...prev, company_legal_name: e.target.value }))}
                    placeholder="Örn: ABC Teknoloji Anonim Şirketi"
                    style={{
                      width: '100%', padding: '10px', border: '1px solid #E5E7EB',
                      borderRadius: '6px', fontSize: '14px'
                    }}
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    Vergi levhasında yazılı resmi şirket ünvanını girin
                  </p>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Vergi Dairesi <span style={{ fontSize: '12px', fontWeight: '400', color: '#6B7280' }}>(İsteğe bağlı)</span>
                  </label>
                  <input
                    type="text"
                    value={convertFormData.tax_office}
                    onChange={(e) => setConvertFormData(prev => ({ ...prev, tax_office: e.target.value }))}
                    placeholder="Örn: Kadıköy Vergi Dairesi"
                    style={{
                      width: '100%', padding: '10px', border: '1px solid #E5E7EB',
                      borderRadius: '6px', fontSize: '14px'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    KDV Numarası / Vergi Numarası <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={convertFormData.tax_number}
                    onChange={(e) => setConvertFormData(prev => ({ ...prev, tax_number: e.target.value }))}
                    placeholder="Örn: 1234567890"
                    style={{
                      width: '100%', padding: '10px', border: '1px solid #E5E7EB',
                      borderRadius: '6px', fontSize: '14px'
                    }}
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    10 haneli vergi numarasını girin
                  </p>
                </div>
              </div>
            </div>
            
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', padding: '12px', backgroundColor: '#DBEAFE', borderRadius: '6px', borderLeft: '4px solid #3B82F6' }}>
              ℹ️ Tüm lead bilgileri (şirket, adres, yetkili kişiler, notlar) müşteri profiline aktarılacak
            </p>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowConvertModal(false);
                  setSelectedLead(null);
                  setConvertFormData({ company_legal_name: '', tax_office: '', tax_number: '' });
                }}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#F3F4F6',
                  color: '#374151', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                İptal
              </button>
              <button
                onClick={handleConvertToCustomer}
                disabled={converting}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#10B981',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                  opacity: converting ? 0.5 : 1
                }}
              >
                {converting ? 'İşleniyor...' : '✅ Müşteriye Çevir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;